import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { safeSessionStorage } from '../utils/storage';
import * as popupService from '../api/popupService';
import logger from '../utils/logger';

const Popup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPopups = async () => {
      try {
        const response = await popupService.getAllPopups();
        const activePopups = response.data?.popups || [];
        
        if (activePopups.length > 0) {
          // Get the first active popup (sorted by displayOrder in backend)
          const popup = activePopups[0];
          
          // Check if this popup should be shown
          if (popup.showOnce) {
            const hasShown = safeSessionStorage.getItem(`popup_${popup._id}_shown`);
            if (hasShown) {
              setLoading(false);
              return;
            }
          }
          
          setCurrentPopup(popup);
          
          // Small delay to ensure smooth loading
          setTimeout(() => {
            setIsOpen(true);
            if (popup.showOnce) {
              safeSessionStorage.setItem(`popup_${popup._id}_shown`, 'true');
            }
          }, 1000);
        }
      } catch (error) {
        logger.error('Error fetching popups:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPopups();
  }, []);

  const closePopup = () => {
    setIsOpen(false);
  };

  const handleImageClick = () => {
    if (currentPopup?.link) {
      if (currentPopup.link.startsWith('http')) {
        window.open(currentPopup.link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = currentPopup.link;
      }
    }
  };

  if (loading || !currentPopup) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePopup}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Popup Content */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
          >
            <button
              onClick={closePopup}
              className="absolute top-2 right-2 p-2 bg-white/80 hover:bg-white text-gray-600 hover:text-red-500 rounded-full transition-colors z-10"
              aria-label="Close popup"
            >
              <X size={24} />
            </button>
            
            <div className="p-1">
              <img 
                src={currentPopup.image?.url} 
                alt={currentPopup.title}
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Popup;

